import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  XMarkIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  PencilIcon,
  CheckIcon,
  ArrowPathIcon
} from "@heroicons/react/24/outline";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getDocumentPreview,
  getDocumentDownloadSAS,
  getDocumentPreviewWithProgress,
  canPreviewDocument,
  getDocumentPreviewType,
  DownloadProgressCallback
} from "./services/documentPreviewService";
import DraftMetadataPreview_Component from "./DraftMetadataPreview_Component";
import DraftOutlinePreview_Component from "./DraftOutlinePreview_Component";
import DraftContentPreview_Component from "./DraftContentPreview_Component";
import YamlDocumentPreview_Component from "./YamlDocumentPreview_Component";
import {
  highlightRequestToFactHighlights,
  getActiveHighlightId,
  buildHighlightRequest,
  HighlightRequest,
  FactHighlight,
  FactHighlightSource
} from "./services/factPreviewService";
import { fetchDraftContent, clearDraftCache } from "./services/draftEditingService";
import { uploadDraftToProject } from "./services/draftUploadService";
import { selectCurrentProjectId } from "./ProjectSlice_Store";
import { selectActiveHighlightRequest, setActiveHighlightRequest, selectInvalidationCounter, selectIsFullScreen, setFullScreen } from "./DocumentPreviewSlice_Store";
import ExpandedDocumentView from "./ExpandedDocumentView_Component";
import { acquireDelegatedGraphToken } from "./utils/msalTokenService";
import PdfFactViewer, { CitationSearchProgress } from "./components/pdf/PdfFactViewer";
import ResizableDividerHorizontal from "./ResizableDividerHorizontal_Component";
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";

// Document type (camelCase only - consistent with types/redux.ts)
interface DocumentData {
  docId?: string;
  originalFilename?: string;
  documentName?: string;
  documentCategory?: string;
  summaryText?: string;
  mimeType?: string;
  fileSize?: number;
  processingStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  lastUpdatedBy?: string;
  documentOrigin?: string;
  filePath?: string;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
}

// Preview data type
interface PreviewData {
  contentBlob: Blob;
  sasData?: {
    source?: string;
    sasUrl?: string;
    isSharePoint?: boolean;
  };
}

// Error type
interface ErrorData {
  message: string;
  type?: string;
  code?: string;
}

// Fact type
interface FactData {
  factId?: string;
  documentId?: string;
  rationale?: string;
  factSourceType?: string;
  highlightContentString?: string;
  surroundingContentString?: string;
  factFromImage?: string;
  pageIndex?: number;
  pageIndices?: number[];
}

interface DocumentPreviewTabProps {
  document: DocumentData | null;
  onClose: () => void;
}

export default function DocumentPreviewTab_Component({
  document,
  onClose
}: DocumentPreviewTabProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorData | null>(null);
  const [contentText, setContentText] = useState<string>('');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loadedDocId, setLoadedDocId] = useState<string | null>(null);

  // Draft editing state
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [originalContent, setOriginalContent] = useState<string>('');
  const [editContent, setEditContent] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  // Open in SharePoint state
  const [opening, setOpening] = useState<boolean>(false);

  // SharePoint download progress state
  const [downloadProgress, setDownloadProgress] = useState<{
    isDownloading: boolean;
    loaded: number;
    total: number | null;
    percentage: number | null;
  }>({ isDownloading: false, loaded: 0, total: null, percentage: null });

  const projectId = useAppSelector(selectCurrentProjectId);
  const currentUser = useAppSelector(state => state.userAuthAndAdminSlice);
  const highlightRequest = useAppSelector(selectActiveHighlightRequest) as HighlightRequest | null;

  // Watch for document invalidation (e.g., when outline/content is regenerated)
  const docId = document?.docId;
  const invalidationCount = useAppSelector(docId ? selectInvalidationCounter(docId) : () => 0);
  const isFullScreen = useAppSelector(selectIsFullScreen);

  // Reset fullscreen state on unmount so it doesn't leak to the next preview
  useEffect(() => {
    return () => { dispatch(setFullScreen(false)); };
  }, [dispatch]);

  // Phase 4: Get all document facts from highlightRequest
  // InlineCitationBadge now attaches ALL facts for the document, not just the clicked one
  const allDocumentFacts = (highlightRequest?.allDocumentFacts as FactData[] | undefined) || [];
  const clickedFact = (highlightRequest?.fact as FactData | undefined) || null;
  const factIdToIndex = highlightRequest?.factIdToIndex; // Chat numbering map (factId -> display index)

  // Sort facts by their display index from factIdToIndex (ascending order)
  const sortedDocumentFacts = useMemo(() => {
    if (!factIdToIndex || allDocumentFacts.length === 0) {
      return allDocumentFacts;
    }
    return [...allDocumentFacts].sort((a, b) => {
      const indexA = a.factId ? (factIdToIndex[a.factId] ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
      const indexB = b.factId ? (factIdToIndex[b.factId] ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });
  }, [allDocumentFacts, factIdToIndex]);

  const documentType = getDocumentPreviewType(document);
  const canPreview = canPreviewDocument(document);
  const isDocDrafterDoc = document?.documentCategory === 'draft';
  const isDirty = isEditing && editContent !== originalContent;

  // Check if this is a doc-drafter draft file (outline, metadata, content) - hide edit/refresh buttons for these
  const filename = document?.originalFilename || document?.documentName || '';
  const isDocDrafterDraftFile = ['draft_outline.yaml', 'draft_metadata.yaml', 'draft_content.yaml'].some(
    name => filename.toLowerCase().includes(name.toLowerCase())
  );

  // Helper to get filename and docId
  const getFilename = (): string => document?.originalFilename || document?.documentName || 'Unknown';
  const getDocId = (): string | undefined => document?.docId;

  // PDF viewer state for citation highlights
  const [pdfViewerReady, setPdfViewerReady] = useState<boolean>(false);
  const [factHighlights, setFactHighlights] = useState<FactHighlight[]>([]);
  const [activeHighlightId, setActiveHighlightId] = useState<string | null>(null);
  const [isFindingCitation, setIsFindingCitation] = useState<boolean>(false);
  const [citationSearchProgress, setCitationSearchProgress] = useState<CitationSearchProgress | null>(null);
  // Monotonic counter: forces PdfFactViewer to re-search even when activeHighlightId is unchanged
  const [searchTrigger, setSearchTrigger] = useState<number>(0);

  // Resizable facts panel state
  const [factsPanelHeight, setFactsPanelHeight] = useState<number>(180);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Ref to store fact row elements for scrolling
  const factRowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const currentDocId = getDocId();

    // Only load if we have all required data AND the document ID has changed
    if (document && canPreview && projectId && currentUser?.userEmail) {
      // Check if this is a different document than the one currently loaded
      if (currentDocId !== loadedDocId) {
        // Reset citation finding state when switching documents
        setIsFindingCitation(false);
        setCitationSearchProgress(null);
        loadDocumentPreview();
        setLoadedDocId(currentDocId);
      }
    }
  }, [document, projectId, currentUser, loadedDocId]);

  // Reload document when invalidation counter changes (e.g., after generate_outline)
  useEffect(() => {
    if (invalidationCount > 0 && document && canPreview && projectId && currentUser?.userEmail) {
      // Clear any cached content for doc-drafter documents
      if (isDocDrafterDoc && docId) {
        clearDraftCache(docId);
      }
      loadDocumentPreview();
    }
  }, [invalidationCount]);

  useEffect(() => {
    // Cleanup object URL when component unmounts or preview data changes
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  // Handle highlight request changes
  useEffect(() => {
    if (highlightRequest && !highlightRequest.isTabular) {
      const highlights = highlightRequestToFactHighlights(highlightRequest);
      setFactHighlights(highlights as FactHighlight[]);

      const activeId = getActiveHighlightId(highlightRequest);
      setActiveHighlightId(activeId);

      if (activeId) {
        setIsFindingCitation(true);
        // Increment trigger so PdfFactViewer re-searches even if activeHighlightId is unchanged
        setSearchTrigger(prev => prev + 1);
      }
    } else {
      setFactHighlights([]);
      setActiveHighlightId(null);
      setIsFindingCitation(false);
      setCitationSearchProgress(null);
    }
  }, [highlightRequest]);

  // Safety net: clear stuck "finding" state after 10s of no progress
  useEffect(() => {
    if (!isFindingCitation) return;

    const safetyTimeout = setTimeout(() => {
      setIsFindingCitation(false);
      setCitationSearchProgress(null);
    }, 10000);

    return () => clearTimeout(safetyTimeout);
  }, [isFindingCitation, citationSearchProgress]);

  // Scroll to the active fact in the facts table when it changes
  // Must wait for loading to complete so the facts panel is rendered and refs are populated
  useEffect(() => {
    // Don't try to scroll while still loading - the facts panel isn't rendered yet
    if (loading) {
      return;
    }

    if (clickedFact && clickedFact.factId && sortedDocumentFacts.length > 0) {
      // Use a delay to ensure facts panel is rendered and refs are populated after loading completes
      const timeoutId = setTimeout(() => {
        const factElement = factRowRefs.current[clickedFact.factId];

        if (factElement) {
          // Find the scrollable container (the facts list div)
          const scrollContainer = factElement.closest('.overflow-y-auto');

          if (scrollContainer) {
            // Calculate scroll position to center the element
            const containerRect = scrollContainer.getBoundingClientRect();
            const elementRect = factElement.getBoundingClientRect();
            const scrollTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top) - (containerRect.height / 2) + (elementRect.height / 2);
            scrollContainer.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth'
            });
          } else {
            // Fallback to scrollIntoView
            factElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [clickedFact, sortedDocumentFacts, loading]);

  const loadDocumentPreview = async () => {
    setLoading(true);
    setError(null);

    try {

      // Extract username from email (everything before @)
      const username = currentUser.userEmail.split('@')[0];

      // For PDFs from blob storage: Use SAS URL directly (enables lazy loading via range requests)
      // This avoids downloading the entire PDF upfront - PDF.js will fetch pages on demand
      if (documentType === 'pdf') {
        const sasData = await getDocumentDownloadSAS(document, projectId, username);

        // If we have a SAS URL (blob storage), use it directly without downloading
        if (sasData.sasUrl && !sasData.isSharePoint) {
          setPreviewData({
            contentBlob: new Blob(), // Empty blob - we'll use the URL instead
            sasData: sasData
          });
          setObjectUrl(sasData.sasUrl);
          return;
        }

        // For SharePoint PDFs: Download with progress tracking
        if (sasData.isSharePoint) {
          // Start at 0% - will update as data comes in
          setDownloadProgress({ isDownloading: true, loaded: 0, total: null, percentage: 0 });

          const onProgress: DownloadProgressCallback = (progress) => {
            setDownloadProgress({
              isDownloading: true,
              loaded: progress.loaded,
              total: progress.total,
              // If server doesn't provide content-length, percentage will be null
              // In that case, show indeterminate progress
              percentage: progress.percentage
            });
          };

          const preview = await getDocumentPreviewWithProgress(document, projectId, username, onProgress);
          setDownloadProgress({ isDownloading: false, loaded: 0, total: null, percentage: null });
          setPreviewData(preview);

          const url = URL.createObjectURL(preview.contentBlob);
          setObjectUrl(url);
          return;
        }
      }

      // For non-PDF files: Download the full content
      const preview = await getDocumentPreview(document, projectId, username);
      setPreviewData(preview);

      // Create object URL for blob content
      const url = URL.createObjectURL(preview.contentBlob);
      setObjectUrl(url);

      // For text-based content, read as text
      if (documentType === 'markdown' || documentType === 'text' ||
          documentType === 'yaml' || documentType === 'draft_metadata' ||
          documentType === 'draft_content' || documentType === 'draft_outline') {
        const text = await preview.contentBlob.text();
        setContentText(text);
      }

    } catch (err: unknown) {
      // Reset download progress on error
      setDownloadProgress({ isDownloading: false, loaded: 0, total: null, percentage: null });

      // Phase 4: Detect SharePoint-specific error codes and provide actionable messaging
      const error = err as { code?: string; message?: string };
      if (error.code === 'SHAREPOINT_DENIED') {
        setError({
          message: 'Your SharePoint access was revoked. Please contact an administrator or open the file directly in SharePoint.',
          type: 'sharepoint_denied',
          code: error.code
        });
      } else if (error.code === 'MISSING_IDENTIFIERS') {
        setError({
          message: 'SharePoint document identifiers are missing. Please refresh the project context and try again.',
          type: 'missing_identifiers',
          code: error.code
        });
      } else {
        // Standard error message for other cases
        setError({
          message: error.message || 'Unknown error',
          type: 'generic'
        });
      }
    } finally {
      setLoading(false);
      // Ensure progress is reset
      setDownloadProgress({ isDownloading: false, loaded: 0, total: null, percentage: null });
    }
  };

  const handleDownload = () => {
    if (objectUrl && document) {
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = getFilename();
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  };

  // Derived: can we open this document externally?
  const hasSharePointIds = !!(document?.sharepointSiteId && document?.sharepointDriveId && document?.sharepointItemId);
  const hasDirectUrl = !!(document?.filePath && /^https?:\/\//i.test(document.filePath));
  const canOpen = hasDirectUrl || hasSharePointIds;

  const handleOpenDocument = async () => {
    const doc = document;
    if (!doc) return;

    if (hasDirectUrl) {
      window.open(doc.filePath, "_blank", "noopener,noreferrer");
      return;
    }
    if (hasSharePointIds) {
      setOpening(true);
      try {
        const graphToken = await acquireDelegatedGraphToken();
        const graphUrl = `https://graph.microsoft.com/v1.0/sites/${doc.sharepointSiteId}/drives/${doc.sharepointDriveId}/items/${doc.sharepointItemId}?$select=webUrl`;
        const res = await fetch(graphUrl, {
          headers: { Authorization: `Bearer ${graphToken}` },
        });
        if (!res.ok) throw new Error(`Graph API returned ${res.status}`);
        const data = await res.json();
        if (data.webUrl) {
          window.open(data.webUrl, "_blank", "noopener,noreferrer");
        }
      } catch (err) {
        console.error("Failed to get SharePoint web URL:", err);
      } finally {
        setOpening(false);
      }
    }
  };

  const handleRetry = () => {
    loadDocumentPreview();
  };

  // PDF viewer ready callback
  const handlePdfViewerReady = () => {
    setPdfViewerReady(true);
  };

  // Citation search callbacks
  const handleCitationSearchStart = useCallback((totalPages: number) => {
    setIsFindingCitation(true);
    setCitationSearchProgress({ currentPage: 0, totalPages, percentage: 0 });
  }, []);

  const handleCitationSearchProgress = useCallback((progress: CitationSearchProgress) => {
    setCitationSearchProgress(progress);
  }, []);

  const handleCitationSearchComplete = useCallback((success: boolean) => {
    setIsFindingCitation(false);
    setCitationSearchProgress(null);
  }, []);

  // Phase 4: Handle clicking a fact row to switch the active highlight
  const handleFactClick = useCallback((clickedFact: FactData, factIndex: number): void => {
    if (!allDocumentFacts || allDocumentFacts.length === 0) {
      return;
    }

    // Rebuild the highlight request with the newly clicked fact as preferred
    const factCompatibleObject: FactHighlightSource = {
      documentId: clickedFact.documentId,
      facts: allDocumentFacts as any
    };

    const newHighlightRequest = buildHighlightRequest(factCompatibleObject, factIndex);

    // Attach metadata
    if (newHighlightRequest) {
      newHighlightRequest.fact = clickedFact;
      newHighlightRequest.factId = clickedFact.factId;
      newHighlightRequest.allDocumentFacts = allDocumentFacts;
      // Derive factSourceType using the same logic as buildHighlightRequest
      const derivedFactSourceType: 'text' | 'image' =
        (clickedFact.factSourceType === 'tabular' ? 'text' : clickedFact.factSourceType as 'text' | 'image') ||
        (clickedFact.factFromImage ? 'image' : 'text');
      newHighlightRequest.factSourceType = derivedFactSourceType;
    }

    // Update the active highlight request in the store
    dispatch(setActiveHighlightRequest(newHighlightRequest));
  }, [allDocumentFacts, document, dispatch]);

  // Handle dragging the horizontal divider to resize facts panel
  const handleDividerDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDividerDragMove = useCallback((clientY: number): void => {
    // Calculate new height based on mouse position relative to viewport
    const viewportHeight = window.innerHeight;
    const newHeight = viewportHeight - clientY;

    // Constrain between min (120px - just header) and max (80% of viewport)
    const minHeight = 120;
    const maxHeight = viewportHeight * 0.8;
    const constrainedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

    setFactsPanelHeight(constrainedHeight);
  }, []);

  const handleDividerDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);


  // Draft editing handlers
  const handleEditClick = async () => {
    if (isDocDrafterDoc && !isEditing) {
      setLoading(true);
      setError(null);

      try {
        const username = currentUser.userEmail.split('@')[0];
        const draftData = await fetchDraftContent({
          projectId,
          doc: { ...document, docId: document.docId || '' } as any,
          username
        });

        setOriginalContent(draftData.text);
        setEditContent(draftData.text);
        setIsEditing(true);
      } catch (err: unknown) {
        setError({ message: err instanceof Error ? err.message : 'Failed to fetch draft content', type: 'generic' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
    setOriginalContent('');
    setSaveError(null);
    setShowPreview(false);
  };

  const handleSaveDraft = async () => {
    if (!isDirty || saving) return;

    setSaving(true);
    setSaveError(null);

    try {
      // Extract username from email (everything before @)
      const username = currentUser.userEmail.split('@')[0];

      await uploadDraftToProject({
        content: editContent,
        filename: getFilename(),
        projectId,
        username
      });

      // Update original content and clear dirty state
      setOriginalContent(editContent);

      // Clear cache for this draft
      clearDraftCache(getDocId());

      // Show success message (could be replaced with toast/snackbar)

      // Exit edit mode
      setIsEditing(false);
      setEditContent('');
      setOriginalContent('');

      // Reload preview
      loadDocumentPreview();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  if (!document) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <DocumentIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>No document selected</p>
        </div>
      </div>
    );
  }

  if (!canPreview) {
    return (
      <div className="flex flex-col h-full">
        {/* Content */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4 text-yellow-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Preview Not Available
            </h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md">
              Preview is only available for PDF and Markdown files. This document type is not supported for preview.
            </p>
            <p className="text-xs text-gray-500">
              File: {getFilename()}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Refresh handler
  const handleRefresh = () => {
    loadDocumentPreview();
  };

  return (
    <div className="flex flex-col h-full bg-cvc-canvas">
      {/* Header with action buttons for doc-drafter documents */}
      {isDocDrafterDoc && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <DocumentIcon className="h-5 w-5 text-cvc-blue" />
            <div>
              <h3 className="text-sm font-medium text-gray-900">{getFilename()}</h3>
              {document.updatedAt && (
                <p className="text-xs text-gray-500">
                  Last updated: {new Date(document.updatedAt).toLocaleString()}
                  {document.lastUpdatedBy && ` by ${document.lastUpdatedBy}`}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Hide refresh and edit buttons for doc-drafter draft files (outline, metadata, content) */}
            {!isDocDrafterDraftFile && (
              <>
                {/* Refresh button */}
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh document"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </>
            )}

            {/* Edit buttons - but NOT for draft_content (uses checkbox selection instead) or doc-drafter draft files */}
            {documentType !== 'draft_content' && !isDocDrafterDraftFile && (
              <>
                {isEditing ? (
                  <>
                    {isDirty && (
                      <span className="text-xs text-orange-600 font-medium">Unsaved changes</span>
                    )}
                    {showPreview && (
                      <button
                        onClick={() => setShowPreview(false)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Edit
                      </button>
                    )}
                    {!showPreview && (
                      <button
                        onClick={() => setShowPreview(true)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        <EyeIcon className="h-4 w-4 inline mr-1" />
                        Preview
                      </button>
                    )}
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveDraft}
                      disabled={!isDirty || saving}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="h-4 w-4 mr-1" />
                          Save
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleEditClick}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit Draft
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Error message for save errors */}
      {saveError && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-600">Save failed: {saveError}</p>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              {downloadProgress.isDownloading ? (
                // Progress-style UI for SharePoint download
                <>
                  <div className="mb-4">
                    <DocumentIcon className="h-12 w-12 mx-auto text-cvc-blue mb-2" />
                    <p className="text-sm font-medium text-gray-900 mb-1">Downloading from SharePoint</p>
                    <p className="text-xs text-gray-500 mb-4">
                      {downloadProgress.total
                        ? `${(downloadProgress.loaded / 1024 / 1024).toFixed(1)} MB of ${(downloadProgress.total / 1024 / 1024).toFixed(1)} MB`
                        : `${(downloadProgress.loaded / 1024 / 1024).toFixed(1)} MB downloaded`
                      }
                    </p>
                  </div>
                  {/* Progress bar */}
                  <div className="w-64 mx-auto">
                    <div className="bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`bg-cvc-blue h-2.5 rounded-full transition-all duration-300 ease-out ${
                          downloadProgress.percentage === null ? 'animate-pulse' : ''
                        }`}
                        style={{
                          width: downloadProgress.percentage !== null
                            ? `${downloadProgress.percentage}%`
                            : '30%'
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {downloadProgress.percentage !== null
                        ? `${downloadProgress.percentage}% complete`
                        : 'Downloading...'
                      }
                    </p>
                  </div>
                </>
              ) : (
                // Standard loading spinner for non-SharePoint documents
                <>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cvc-blue mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600">Loading document preview...</p>
                </>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center max-w-lg">
              <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4 text-red-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Preview Error
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {typeof error === 'string' ? error : error.message}
              </p>
              {/* Phase 4: SharePoint-specific guidance */}
              {error.type === 'sharepoint_denied' && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-left">
                  <p className="text-xs text-yellow-800 font-medium mb-2">What you can do:</p>
                  <ul className="text-xs text-yellow-700 space-y-1 list-disc list-inside">
                    <li>Contact your SharePoint administrator to restore access</li>
                    <li>Open the file directly in SharePoint if you still have web access</li>
                    <li>Request the document owner to reshare the file with you</li>
                  </ul>
                </div>
              )}
              {error.type === 'missing_identifiers' && (
                <div className="mb-4 p-3 bg-cvc-lb2 border border-cvc-lb1 rounded-md text-left">
                  <p className="text-xs text-cvc-blue font-medium mb-2">What you can do:</p>
                  <ul className="text-xs text-cvc-blue space-y-1 list-disc list-inside">
                    <li>Refresh the project context from the sidebar</li>
                    <li>If the issue persists, contact support</li>
                  </ul>
                </div>
              )}
              <div className="flex gap-2 justify-center">
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue"
                >
                  Try Again
                </button>
                {objectUrl && (
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-cvc-text bg-white border border-cvc-grey rounded-md hover:bg-cvc-lb2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : !objectUrl ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <EyeIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-sm text-gray-600">No preview available</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {documentType === 'pdf' ? (
              highlightRequest?.isTabular ? (
                <object
                  data={objectUrl}
                  type="application/pdf"
                  className="w-full h-full"
                  aria-label={`PDF preview of ${getFilename()}`}
                >
                  <div className="flex items-center justify-center h-full p-8">
                    <div className="text-center">
                      <DocumentIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-sm text-gray-600 mb-4">
                        Your browser doesn't support PDF preview.
                      </p>
                      <button
                        onClick={handleDownload}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Download PDF
                      </button>
                    </div>
                  </div>
                </object>
              ) : (
                <>
                  {/* PDF Viewer - takes remaining space */}
                  <div className="flex-1 overflow-hidden min-h-0 relative">
                    {/* Floating toolbar: Expand + Download + Open */}
                    {!isFullScreen && (
                      <div className="absolute top-2 right-5 z-20 flex flex-col gap-1">
                        <button
                          onClick={() => dispatch(setFullScreen(true))}
                          className="p-2 tablet:p-3 bg-white/90 hover:bg-white rounded-lg shadow-md border border-gray-200 transition-colors"
                          title="Expand to full screen"
                          aria-label="Expand to full screen"
                        >
                          <ArrowsPointingOutIcon className="h-5 w-5 text-cvc-blue" />
                        </button>
                        {objectUrl && (
                          <button
                            onClick={handleDownload}
                            className="p-2 tablet:p-3 bg-white/90 hover:bg-white rounded-lg shadow-md border border-gray-200 transition-colors"
                            title="Download"
                            aria-label="Download document"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5 text-cvc-blue" />
                          </button>
                        )}
                        {canOpen && (
                          <button
                            onClick={handleOpenDocument}
                            disabled={opening}
                            className="p-2 tablet:p-3 bg-white/90 hover:bg-white rounded-lg shadow-md border border-gray-200 transition-colors disabled:opacity-50"
                            title="Open document"
                            aria-label="Open document"
                          >
                            <ArrowTopRightOnSquareIcon className="h-5 w-5 text-cvc-blue" />
                          </button>
                        )}
                      </div>
                    )}
                    {/* Finding citation overlay - shown on top of PDF while searching */}
                    {isFindingCitation && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-cvc-canvas">
                        <div className="text-center">
                          <div className="mb-4">
                            <DocumentIcon className="h-12 w-12 mx-auto text-cvc-blue mb-2" />
                            <p className="text-sm font-medium text-gray-900 mb-1">Finding citation...</p>
                          </div>
                          {/* Progress bar */}
                          <div className="w-64 mx-auto">
                            <div className="bg-gray-200 rounded-full h-2.5 overflow-hidden">
                              <div
                                className={`bg-cvc-blue h-2.5 rounded-full transition-all duration-300 ease-out ${
                                  !citationSearchProgress || citationSearchProgress.percentage === 0 ? 'animate-pulse' : ''
                                }`}
                                style={{
                                  width: citationSearchProgress && citationSearchProgress.percentage > 0
                                    ? `${citationSearchProgress.percentage}%`
                                    : '30%'
                                }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              {citationSearchProgress && citationSearchProgress.totalPages > 1
                                ? `Searching page ${citationSearchProgress.currentPage} of ${citationSearchProgress.totalPages}...`
                                : 'Locating text in document...'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {!isFullScreen && (
                      <PdfFactViewer
                        blob={
                          previewData?.sasData?.sasUrl && !previewData?.sasData?.isSharePoint
                            ? previewData.sasData.sasUrl
                            : previewData?.contentBlob
                        }
                        factHighlights={factHighlights}
                        activeHighlightId={activeHighlightId}
                        searchTrigger={searchTrigger}
                        onReady={handlePdfViewerReady}
                        onCitationSearchStart={handleCitationSearchStart}
                        onCitationSearchProgress={handleCitationSearchProgress}
                        onCitationSearchComplete={handleCitationSearchComplete}
                      />
                    )}
                    {isFullScreen && (
                      <ExpandedDocumentView
                        documentName={document?.originalFilename || document?.documentName || 'Document'}
                        filePath={document?.filePath}
                        sharepointSiteId={document?.sharepointSiteId}
                        sharepointDriveId={document?.sharepointDriveId}
                        sharepointItemId={document?.sharepointItemId}
                        blob={
                          previewData?.sasData?.sasUrl && !previewData?.sasData?.isSharePoint
                            ? previewData.sasData.sasUrl
                            : previewData?.contentBlob
                        }
                        factHighlights={factHighlights}
                        activeHighlightId={activeHighlightId}
                        searchTrigger={searchTrigger}
                        objectUrl={objectUrl}
                        onClose={() => dispatch(setFullScreen(false))}
                        onDownload={handleDownload}
                        onReady={handlePdfViewerReady}
                        onCitationSearchStart={handleCitationSearchStart}
                        onCitationSearchProgress={handleCitationSearchProgress}
                        onCitationSearchComplete={handleCitationSearchComplete}
                      />
                    )}
                  </div>
                  {/* Phase 4: Resizable Facts Panel */}
                  {sortedDocumentFacts.length > 0 && (
                    <>
                      {/* Horizontal Divider */}
                      <ResizableDividerHorizontal
                        onDragStart={handleDividerDragStart}
                        onDragMove={handleDividerDragMove}
                        onDragEnd={handleDividerDragEnd}
                      />

                      {/* Facts Panel with fixed height */}
                      <div
                        className="border-t border-gray-200 bg-white flex flex-col flex-shrink-0"
                        style={{ height: `${factsPanelHeight}px` }}
                      >
                        {/* Header - always visible at top */}
                        <div className="flex-shrink-0 px-4 pt-4 pb-2">
                          <h4 className="text-sm font-cvcDisplay font-semibold text-cvc-blue">
                            Facts from this document ({sortedDocumentFacts.length}):
                          </h4>
                        </div>

                        {/* Scrollable facts list */}
                        <div className="flex-1 overflow-y-auto px-4 pb-4">
                          <div className="space-y-2">
                            {sortedDocumentFacts.map((fact, index) => {
                              const isClickedFact = clickedFact && fact.factId === clickedFact.factId;
                              const sourceType = fact.factSourceType === 'image' ? 'image' : 'text';
                              // Both image and text facts now use highlightContentString
                              const rawSnippet =
                                fact.highlightContentString ||
                                fact.surroundingContentString ||
                                fact.factFromImage ||  // Legacy fallback for older image facts
                                '';
                              const hasSnippet = Boolean(rawSnippet);
                              const displaySnippet =
                                rawSnippet && rawSnippet.length > 280
                                  ? `${rawSnippet.slice(0, 277)}…`
                                  : rawSnippet;
                              // Use chat numbering if available, otherwise fall back to array index
                              const displayNumber = fact.factId
                                ? (factIdToIndex?.[fact.factId] ?? (index + 1))
                                : (index + 1);
                              return (
                                <div
                                  key={fact.factId || index}
                                  ref={(el) => {
                                    if (fact.factId && el) {
                                      factRowRefs.current[fact.factId] = el;
                                    }
                                  }}
                                  onClick={() => handleFactClick(fact, index)}
                                  className={`p-3 rounded border cursor-pointer transition-all hover:bg-cvc-lb2 ${
                                    isClickedFact
                                      ? 'bg-cvc-lb1 border-cvc-blue ring-2 ring-cvc-blue ring-offset-1'
                                      : 'bg-gray-50 border-cvc-grey hover:border-cvc-lb1'
                                  }`}
                                >
                                  <div className="flex items-start">
                                    <span
                                      className={`inline-flex items-center justify-center w-6 h-6 mr-3 text-xs font-semibold rounded-full flex-shrink-0 ${
                                        isClickedFact
                                          ? 'text-white bg-cvc-blue'
                                          : 'text-cvc-text bg-gray-200'
                                      }`}
                                    >
                                      {displayNumber}
                                    </span>
                                    <div className="flex-1">
                                      <div className="flex items-start gap-2 mb-1">
                                        <p className="text-sm text-gray-700 font-medium flex-1">
                                          {fact.rationale || "No rationale provided"}
                                        </p>
                                        <span
                                          className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                            sourceType === 'image'
                                              ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                              : 'bg-green-100 text-green-700 border border-green-200'
                                          }`}
                                        >
                                          {sourceType === 'image' ? 'Image analysis' : 'Text analysis'}
                                        </span>
                                      </div>
                                      {hasSnippet && (
                                        <p className="text-xs text-gray-500 italic">
                                          "{displaySnippet}"
                                        </p>
                                      )}
                                      {/* Display page info - prefer pageIndices (multi-page) over single pageIndex */}
                                      {(fact.pageIndices && fact.pageIndices.length > 0) ? (
                                        <p className="text-xs text-gray-400 mt-2">
                                          {fact.pageIndices.length === 1
                                            ? `Page ${fact.pageIndices[0] + 1}`
                                            : `Pages ${fact.pageIndices.map(p => p + 1).join(', ')}`}
                                        </p>
                                      ) : (fact.pageIndex !== undefined && fact.pageIndex !== null) && (
                                        <p className="text-xs text-gray-400 mt-2">
                                          Page {fact.pageIndex + 1}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )
            ) : documentType === 'markdown' ? (
              // Show editor for drafts in edit mode
              isEditing ? (
                <div className="h-full flex flex-col">
                  {showPreview ? (
                    // Preview mode
                    <div className="h-full overflow-auto p-6 bg-white">
                      <div className="max-w-4xl mx-auto">
                        <div className="prose prose-sm max-w-none">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({children}) => <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-4 first:mt-0">{children}</h1>,
                            h2: ({children}) => <h2 className="text-xl font-bold text-gray-900 mt-5 mb-3">{children}</h2>,
                            h3: ({children}) => <h3 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h3>,
                            p: ({children}) => <p className="text-gray-700 mb-3 leading-relaxed">{children}</p>,
                            ul: ({children}) => <ul className="list-disc list-inside mb-3 text-gray-700">{children}</ul>,
                            ol: ({children}) => <ol className="list-decimal list-inside mb-3 text-gray-700">{children}</ol>,
                            blockquote: ({children}) => <blockquote className="border-l-4 border-blue-200 pl-4 my-3 text-gray-600 italic">{children}</blockquote>,
                            code: ({children, ...props}) => {
                              const isInline = !props.className || !props.className.startsWith('language-');
                              return isInline ?
                                <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-gray-800">{children}</code> :
                                <code className="block bg-gray-100 p-3 rounded text-sm font-mono text-gray-800 overflow-x-auto">{children}</code>;
                            },
                            pre: ({children}) => <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">{children}</pre>,
                          }}
                        >
                          {editContent}
                        </ReactMarkdown>
                      </div>
                      </div>
                    </div>
                  ) : (
                    // Edit mode
                    <div className="h-full p-4 bg-gray-50">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full h-full p-4 font-mono text-sm text-cvc-text bg-white border border-cvc-grey rounded-lg resize-none focus:ring-2 focus:ring-cvc-blue focus:border-transparent"
                        placeholder="Write your markdown content here..."
                      />
                    </div>
                  )}
                </div>
              ) : (
                // Normal preview mode (not editing)
                <div className="h-full overflow-auto p-6 bg-white">
                  <div className="max-w-4xl mx-auto">
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Custom styling for markdown elements
                          h1: ({children}) => <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-4 first:mt-0">{children}</h1>,
                          h2: ({children}) => <h2 className="text-xl font-bold text-gray-900 mt-5 mb-3">{children}</h2>,
                          h3: ({children}) => <h3 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h3>,
                          p: ({children}) => <p className="text-gray-700 mb-3 leading-relaxed">{children}</p>,
                          ul: ({children}) => <ul className="list-disc list-inside mb-3 text-gray-700">{children}</ul>,
                          ol: ({children}) => <ol className="list-decimal list-inside mb-3 text-gray-700">{children}</ol>,
                          blockquote: ({children}) => <blockquote className="border-l-4 border-blue-200 pl-4 my-3 text-gray-600 italic">{children}</blockquote>,
                          code: ({children, ...props}) => {
                            const isInline = !props.className || !props.className.startsWith('language-');
                            return isInline ?
                              <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-gray-800">{children}</code> :
                              <code className="block bg-gray-100 p-3 rounded text-sm font-mono text-gray-800 overflow-x-auto">{children}</code>;
                          },
                          pre: ({children}) => <pre className="bg-gray-100 p-3 rounded mb-3 overflow-x-auto">{children}</pre>,
                        }}
                      >
                        {contentText}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              )
            ) : documentType === 'text' ? (
              <div className="h-full overflow-auto p-6 bg-white">
                <div className="max-w-4xl mx-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
                    {contentText}
                  </pre>
                </div>
              </div>
            ) : documentType === 'draft_metadata' ? (
              <DraftMetadataPreview_Component contentText={contentText} />
            ) : documentType === 'draft_content' ? (
              <DraftContentPreview_Component contentText={contentText} />
            ) : documentType === 'draft_outline' ? (
              <DraftOutlinePreview_Component contentText={contentText} />
            ) : documentType === 'yaml' ? (
              <YamlDocumentPreview_Component contentText={contentText} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <DocumentIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-sm text-gray-600">Unknown document type</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
